import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search as SearchIcon, KeyRound, ChevronRight, Check } from 'lucide-react'
import { api } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import { useSearchStore } from '../../stores/useSearchStore.js'
import TapSecret from '../logins/TapSecret.jsx'
import { LogSearchSections } from '../sessionlog/ProjectsPage.jsx'

/**
 * The landing screen. The whole corpus is ~130 pages, so results are
 * effectively instant — no spinner, results update per keystroke (debounced
 * just enough to skip intermediate states).
 *
 * Two places to look, ticked under the box (Charles, 2026-09-13):
 * Information (pages, logins, the audiobooks tracker — /api/search) and
 * Session Logs (sessions and multi-day projects — /api/log/search). Either
 * or both; the two searches stay separate server-side on purpose, so this
 * screen simply asks each one that's ticked.
 */
const SCOPE_OPTIONS = [
  { key: 'info', label: 'Information' },
  { key: 'log', label: 'Session Logs' },
]

export default function SearchPage() {
  const q = useSearchStore((s) => s.q)
  const setQ = useSearchStore((s) => s.setQ)
  const scope = useSearchStore((s) => s.scope)
  const toggleScope = useSearchStore((s) => s.toggleScope)
  const [debounced, setDebounced] = useState(q)
  const inputRef = useRef(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 120)
    return () => clearTimeout(t)
  }, [q])

  const hasQuery = debounced.trim().length > 0

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api(`/search/?q=${encodeURIComponent(debounced)}`),
    enabled: hasQuery && scope.info,
    placeholderData: (prev) => prev, // keep old results while typing
  })
  // Same cache key as the Session Log home's own search box, so a term
  // looked up there is already warm here and vice versa.
  const logResults = useQuery({
    queryKey: ['sl-search', debounced],
    queryFn: () => api(`/log/search?q=${encodeURIComponent(debounced)}`),
    enabled: hasQuery && scope.log,
    placeholderData: (prev) => prev,
  })

  const pages = scope.info ? results.data?.pages || [] : []
  const logins = scope.info ? results.data?.logins || [] : []
  const jobs = scope.info ? results.data?.jobs || [] : []
  const sessions = scope.log ? logResults.data?.sessions || [] : []
  const projects = scope.log ? logResults.data?.projects || [] : []
  const infoDone = !scope.info || results.isFetched
  const logDone = !scope.log || logResults.isFetched
  const nothing =
    infoDone && logDone &&
    pages.length === 0 && logins.length === 0 && jobs.length === 0 &&
    sessions.length === 0 && projects.length === 0
  const placeholder =
    scope.info && scope.log
      ? 'Search setups, clients, guides, sessions…'
      : scope.log
        ? 'Search sessions, gear, notes…'
        : 'Search setups, clients, guides…'

  return (
    <div>
      <div className="relative">
        <SearchIcon size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-faint" />
        <input
          ref={inputRef}
          autoFocus
          type="search"
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="tap w-full rounded-2xl border border-hairline bg-grouped py-3.5 pl-11 pr-4 text-[16px] text-ink placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
        />
      </div>

      {/* Where to look. Tapping the only ticked box does nothing — the
          store keeps at least one on. */}
      <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Search in">
        {SCOPE_OPTIONS.map(({ key, label }) => {
          const on = Boolean(scope[key])
          return (
            <button
              key={key}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggleScope(key)}
              className={cn(
                'tap flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                on ? 'border-navy bg-white text-ink' : 'border-hairline bg-white text-muted active:bg-grouped'
              )}
              style={{ borderWidth: '1.5px' }}
            >
              <span
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded border',
                  on ? 'border-navy bg-navy text-white' : 'border-faint bg-white'
                )}
                style={{ borderWidth: '1.5px' }}
                aria-hidden
              >
                {on && <Check size={12} strokeWidth={3} />}
              </span>
              {label}
            </button>
          )
        })}
      </div>

      {!hasQuery && (
        <p className="mt-10 text-center text-sm text-muted">
          {scope.info && scope.log
            ? "Sample rates, signal chains, door procedures, logins, past sessions — it's all one search away."
            : scope.log
              ? 'Sessions, gear, chains, notes, narrators — everything the studio has logged.'
              : "Sample rates, signal chains, door procedures, logins — it's all one search away."}
        </p>
      )}

      {hasQuery && (
        <div className="mt-5 space-y-6">
          {logins.length > 0 && (
            <section>
              <h2 className="section-header mb-2">Logins</h2>
              <div className="card divide-y divide-hairline">
                {logins.map((l) => (
                  <div key={l.id} className="flex items-center gap-3 px-4 py-3">
                    <KeyRound size={16} className="shrink-0 text-faint" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink">{l.label}</div>
                      {l.collection === 'embargo' && (
                        <Link to="/clients/embargo-logins" className="block truncate text-xs text-muted hover:text-ink">
                          Embargoed Projects Passwords →
                        </Link>
                      )}
                      {l.username && <div className="truncate text-xs text-muted">{l.username}</div>}
                    </div>
                    <TapSecret credentialId={l.id} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {jobs.length > 0 && (
            <section>
              <h2 className="section-header mb-2">Audiobooks</h2>
              <div className="card divide-y divide-hairline">
                {jobs.map((j) => (
                  <Link key={j.id} to="/post-production" className="flex items-center gap-3 px-4 py-3 active:bg-grouped">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink">{j.title}</div>
                      <div className="mt-0.5 text-xs text-muted">
                        {[j.status, j.publisher].filter(Boolean).join(' · ') || 'no status'}
                      </div>
                    </div>
                    <ChevronRight size={16} className="shrink-0 text-faint" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {pages.length > 0 && (
            <section>
              <h2 className="section-header mb-2">Pages</h2>
              <div className="card divide-y divide-hairline">
                {pages.map((p) => (
                  <Link
                    key={p.id}
                    to={`/${p.space}/${p.slug}`}
                    className="flex items-center gap-3 px-4 py-3 active:bg-grouped"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-sm font-medium text-ink">{p.title}</span>
                        <span className="shrink-0 text-xs text-faint">{p.spaceName}</span>
                      </div>
                      <Snippet text={p.snippet} />
                    </div>
                    <ChevronRight size={16} className="shrink-0 text-faint" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {(sessions.length > 0 || projects.length > 0) && (
            <LogSearchSections sessions={sessions} projects={projects} />
          )}

          {nothing && (
            <p className="mt-10 text-center text-sm text-muted">Nothing found for “{debounced}”.</p>
          )}
        </div>
      )}
    </div>
  )
}

/** FTS snippets arrive with \x01…\x02 around hits — split and <mark> them
 *  client-side so page content is never interpolated into HTML. */
function Snippet({ text }) {
  if (!text) return null
  const parts = text.split(/([\x01\x02])/)
  let inHit = false
  return (
    <div className="mt-0.5 truncate text-xs text-muted">
      {parts.map((part, i) => {
        if (part === '\x01') { inHit = true; return null }
        if (part === '\x02') { inHit = false; return null }
        return inHit ? (
          <mark key={i} className="bg-transparent font-semibold text-link">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      })}
    </div>
  )
}
